import {
  Component,
  OnDestroy,
  OnInit,
  Input,
  NgZone,
  Output,
} from "@angular/core";

import {
  GenericKendoGridColumnDefinition,
  LookupEntityList,
  MetadataSet,
  ParsedFieldMetadataDetail,
  ParsedMetadataSet,
} from "../../../insa-models";
import {
  AuthService,
  InsaMetadataService,
  LocalizeService,
} from "../../../insa-services";
import {
  customGroupHeader,
  CustomGridFilterModule,
  SharedMethodsModule,
} from "../../../insa-custom-scripts";

import "./generic-grid.css";
import { ActivatedRoute, Router } from "@angular/router";
import { makeParsedMetadataSet } from "../utils/parsedMetadataSet";
import computeKeyFields from "../utils/keyFields";
import { combineLatest, Observable } from "rxjs";
import { values } from "core-js/core/array";
import { GridStateService } from "../../../insa-services/gridState.service";

let cloneDeep = require("lodash.clonedeep");

// this number is multiplied with FieldWidth from metadata
const FIELDWIDTH_MULTIPLIER = 5;
const GRID_DATE_SHORT_FORMAT = "dd.MM.yyyy";
const GRID_DATE_LONG_FORMAT = "dd.MM.yyyy hh:mm:ss";
const GRID_DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm";
const GRID_NUMBER_FORMAT = "{0:#.##}";
const GRID_DEFAULT_WIDTH = 120;

@Component({
  selector: "generic-grid",
  templateUrl: "generic-grid.component.html",
  styles: [require("./generic-grid.component.css").toString()],
})
export class GenericGridComponent implements OnInit, OnDestroy {
  @Input() metadataName?: string; // either `data` or `metadataName` required for rendering
  @Input() data?: any[];
  @Input() onItemSelect?: (entityId: number | string) => undefined;
  @Input() canAdd: boolean = true; // overrides metadata settings if allowed
  @Input() canDelete: boolean = true; // overrides metadata settings if allowed

  gridName = "genericGrid";
  private hasKey = false;
  isGridWizardVisible: boolean = false;

  selectedId: any;
  metadataSet?: ParsedMetadataSet;
  keyFields?: string[];
  gridKendoOptions: kendo.ui.GridOptions;
  defaultLayout;
  aggregates = [];

  constructor(
    private route: ActivatedRoute,
    private _router: Router,
    private _zone: NgZone,
    private _insaMetadataService: InsaMetadataService,
    private _activeRoute: ActivatedRoute,
    private _localize: LocalizeService,
    private _authService: AuthService,
    private _gridStateService: GridStateService,
  ) {
    this.doubleClickHandler = this.doubleClickHandler.bind(this);
    this.delPressHandler = this.delPressHandler.bind(this);
    this.openGridWizard = this.openGridWizard.bind(this);
  }

  applyGridSettings(newOptions) {
    const kendoGrid = $("#genericGrid").data("kendoGrid");

    // add customCompare functions if necessary
    newOptions.columns = SharedMethodsModule.CustomizeGroupOrSortSettings(
      kendoGrid,
      newOptions.columns,
    );

    // append action collumns (edit/delete)
    const currOptions = kendoGrid.getOptions();
    const actionColumns = currOptions.columns.filter((col) => !col.field);
    if (actionColumns.length) {
      newOptions.columns.push(...actionColumns);
    }

    kendoGrid.setOptions(newOptions);
    setTimeout(() => {
      $("#custom-grid-wizard-toggle").off("click", this.openGridWizard);
      $("#custom-grid-wizard-toggle").click(this.openGridWizard);
    }, 100);
  }

  // ===GETTERS===
  get routeName() {
    return this._activeRoute.snapshot.params.metadataName;
  }

  deleteItem(entityId) {
    if (this.canDelete) {
      this.loadingStarted();
      this._insaMetadataService
        .deleteGenericEntity(entityId, this.metadataName)
        .subscribe(
          () => {
            this._insaMetadataService
              .getMetaDataInformation(this.metadataName)
              .subscribe((metadataSet: MetadataSet) => {
                const kendoGrid = $("#" + this.gridName).data("kendoGrid");
                if (kendoGrid) {
                  kendoGrid.setDataSource(
                    new kendo.data.DataSource({
                      data: metadataSet[1].MetaDataSelectedEntity,
                    }),
                  );
                  this.loadingStopped();
                }
              });
          },
          (errorMessage) => {
            this.handleError(errorMessage);
          },
        );
    } else {
      alert(`Delete not allowed on items from ${this.metadataName}`);
    }
  }

  // HELPER METHODS
  goToDetailsPage(id: string | number) {
    this._router.navigate([this._router.url, id]);
  }

  doubleClickHandler() {
    const grid = $("#" + this.gridName).data("kendoGrid");
    const selectedItem = grid.dataItem(grid.select());
    if (selectedItem) {
      if (this.keyFields[0]) {
        this.onItemSelect(selectedItem[this.keyFields[0]]);
      } else {
        this.handleError(
          `${this.metadataName} is missing a primary key in its Metadata details`,
        );
      }
    }
  }

  delPressHandler(evt) {
    if (evt.keyCode == 46 || evt.key === "Delete") {
      const grid = $("#" + this.gridName).data("kendoGrid");
      const selectedItem = grid.dataItem(grid.select());
      if (selectedItem) {
        const isOk = confirm("Are you sure you want to delete this record?");
        if (isOk) {
          this.deleteItem(selectedItem[this.keyFields[0]]);
        }
      }
    }
  }

  showModal({ title = "", message = "" }) {
    const modalEl = $("#generic-form-modal");
    modalEl.find(".modal-title").text(title);
    modalEl.find(".modal-body > p").text(message);
    modalEl.modal({ show: true });
  }

  private prepareGenericForm(metadataSet: ParsedMetadataSet, defaultLayout) {
    const recordSet = metadataSet[1].MetaDataSelectedEntity;
    const metadataDetails = metadataSet[0].InsaMetaDataDetails;
    const lookupEntityList = metadataSet[1].MetaDataLookupEntity;
    const formMetadata = metadataSet[0].InsaMetaData[0];

    this.canAdd = this.canAdd ? Boolean(formMetadata.AllowAddNew) : this.canAdd;
    this.canDelete = this.canDelete
      ? Boolean(formMetadata.AllowDelete)
      : this.canDelete;

    let gridSource = this.prepareGridSource(recordSet, metadataSet);

    // make column defs
    const additionalSortingFields = metadataDetails.filter(
      (md) => md.SortAdditionalField,
    );
    let columnDefs;
    if (defaultLayout.options) {
      if (additionalSortingFields.length) {
        additionalSortingFields.forEach((md) => {
          const col = defaultLayout.options.columns.find(
            (dcol) => dcol.field === md.FieldTitle,
          );
          if (col.groupable) {
            col.groupable.sort.compare =
              SharedMethodsModule.makeCustomCompareFunction(
                md.SortAdditionalField,
              );
          }
          if (col.sortable) {
            col.sortable.compare =
              SharedMethodsModule.makeCustomCompareFunction(
                md.SortAdditionalField,
              );
          }
        });
      }
      columnDefs = defaultLayout.options.columns;
    } else {
      columnDefs = this.createGridColumnDef(metadataDetails, lookupEntityList);
    }

    // initialize
    const kendoGrid = $("#" + this.gridName).data("kendoGrid");
    if (kendoGrid) {
      kendoGrid.destroy();
    }
    this.initGrid(columnDefs, gridSource, metadataDetails, defaultLayout);
    this.registerDoubleClickHandler();
    this.registerDelPressHandler();
  }

  prepareGridSource(recordSet, metadataDetails) {
    let data = recordSet;
    let schema = {
      model: {
        id: "",
        fields: {},
      },
    };
    metadataDetails.forEach((md) => {
      if (md.IsKey) {
        schema.model.id = md.FieldName;
      }
      schema.model.fields[md.FieldName] = { type: md.JavascriptType };
    });

    return { data: data, schema: schema };
  }

  private createGridColumnDef(
    metadataDetails: ParsedFieldMetadataDetail[],
    lookupEntityList: LookupEntityList,
  ) {
    const gridColumnDefs = new Array<any>();

    metadataDetails
      .filter((md) => md.VisibleOnGrid && md.OrderID !== 0)
      .forEach((md) => {
        let fmtType = md.JavascriptType;
        let metaFormat = md.Format ? md.Format.trim().toUpperCase() : undefined;
        if (metaFormat === "") {
          metaFormat = undefined;
        }

        const chosenFormat = metaFormat || fmtType;

        const fieldName = md.FieldName.replace(" ", "_");

        const customCompareFunction = md.SortAdditionalField
          ? SharedMethodsModule.makeCustomCompareFunction(
              md.SortAdditionalField,
            )
          : null;

        const title = md.FieldTitle;
        let format: string;
        const filterable = true;
        // const aggregates = md.ShowTotals ? ['sum'] : null;
        let aggregates = null;
        if (md.ShowTotals == true) aggregates = ["sum"];
        if (aggregates) {
          this.aggregates.push({ field: md.FieldName, aggregate: "sum" });
        }
        const footerTemplate = aggregates
          ? '<div style="float: right">#= (sum!==null) ? kendo.toString(sum, "n2") : "" #</div>'
          : null;
        const groupFooterTemaplte = aggregates
          ? '<div style="float: right">#= (sum!==null) ? kendo.toString(sum, "n2") : "" #</div>'
          : null;
        const hidden = !md.DefaultVisibility;
        const groupable = md.GroupEnabled
          ? customCompareFunction
            ? { sort: { compare: customCompareFunction } }
            : md.GroupEnabled
          : false;
        const width = Math.max(
          md.FieldWidth * FIELDWIDTH_MULTIPLIER,
          GRID_DEFAULT_WIDTH,
        );
        const headerAttributes = {};
        const sortable = md.SortEnabled
          ? customCompareFunction
            ? { compare: customCompareFunction }
            : md.SortEnabled
          : false;
        let template: string;
        // const editable = Boolean(md.Locked);
        const editable = false;
        let values;

        switch (chosenFormat) {
          case "DATETIME":
          case "LONGDATE":
          case "dd.MM.yyyy HH:mm:ss":
          case "datetime":
            format = "";
            template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, '${GRID_DATE_TIME_FORMAT}') : '' #`;
            break;
          case "DATE":
          case "dd.MM.yyyy":
          case "date":
            format = "dd.MM.yyyy";
            template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, '${GRID_DATE_SHORT_FORMAT}') : '' #`;
            break;
          case "PERCENT":
            format = `'${GRID_NUMBER_FORMAT} %'`;
            template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName} * 100, 'n2') : '' #`;
            break;
          case "#,##00.00":
          case "NUMBER":
            format = `'${GRID_NUMBER_FORMAT}'`;
            template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, 'n2') : '' #`;
            break;
          case "CHECKBOX":
          case "boolean":
            format = ``;
            template = `<i class="glyphicon glyphicon-#:${md.FieldName} ? "ok" : "remove" #"></i>`;
            break;
          // by default number is not formatted
          case "number":
          case "MAILBUTTON":
          case "SELECTOR":
          case "IMAGE":
          case "string":
          case "":
          case undefined:
          default:
            format = "";
            if (md.LookupTable != null && md.LookupTable.trim() !== "") {
              if (lookupEntityList.length > 0) {
                const filteredLookup = lookupEntityList
                  .filter((le) => le.lookupTableName === md.LookupTable)
                  .map(function (item) {
                    return item.lookupData;
                  });

                const finalLookup = [].concat.apply([], filteredLookup);
                values = finalLookup.map((item) => {
                  let lookupValues: Object = {
                    value: item[md.LookupField],
                    text: item[md.LookupDisplayField],
                  };
                  return lookupValues;
                });
              }
            } else {
              template = `#:(${md.FieldName}!==null) ? ${md.FieldName} : '' #`;
            }
        }

        const column = new GenericKendoGridColumnDefinition(
          fieldName,
          title,
          format,
          filterable,
          undefined,
          aggregates,
          footerTemplate,
          groupFooterTemaplte,
          undefined,
          hidden,
          undefined,
          groupable,
          width,
          headerAttributes,
          sortable,
          template,
          editable,
        );

        if (values) {
          column.values = values;
        }

        if (fieldName === "ContactDate") {
          debugger;
        }

        gridColumnDefs.push(column);
      });
    return gridColumnDefs;
  }

  registerDoubleClickHandler() {
    $("#" + this.gridName).off("dblclick", this.doubleClickHandler);
    if (this.onItemSelect) {
      $("#" + this.gridName).on("dblclick", this.doubleClickHandler);
    }
  }

  registerDelPressHandler() {
    $("html").off("keydown", this.delPressHandler);
    $("html").keydown(this.delPressHandler);
  }

  openGridWizard() {
    this.isGridWizardVisible = true;
  }

  closeGridWizard() {
    this.isGridWizardVisible = false;
  }

  private initGrid(
    columnDefs,
    gridSource,
    metadataDetails: ParsedFieldMetadataDetail[],
    defaultLayout,
  ) {
    let dataSourceOptions: kendo.data.DataSourceOptions = {};
    dataSourceOptions.data = gridSource.data;
    dataSourceOptions.schema = gridSource.schema;
    if (defaultLayout.options && defaultLayout.options.dataSource.group) {
      dataSourceOptions.group =
        SharedMethodsModule.CustomizeGroupOrSortSettings(
          { columns: columnDefs },
          defaultLayout.options.dataSource.group,
        );
    }
    if (defaultLayout.options && defaultLayout.options.dataSource.sort) {
      dataSourceOptions.sort = SharedMethodsModule.CustomizeGroupOrSortSettings(
        { columns: columnDefs },
        defaultLayout.options.dataSource.sort,
      );
    }
    dataSourceOptions.aggregate = metadataDetails
      .filter((md) => md.ShowTotals)
      .map((md) => ({ field: md.FieldName, aggregate: "sum" }));

    dataSourceOptions.transport = {
      read: function (e) {
        e.success(gridSource.data);
      },
      update: function (e) {},
      create: function (e) {},
      destroy: function (e) {},
    };
    let dataSource: kendo.data.DataSource = new kendo.data.DataSource(
      dataSourceOptions,
    );

    // let kendoGrid: kendo.ui.Grid = $('#genericGrid').data('kendoGrid');
    let kendoGrid: kendo.ui.Grid = $("#" + this.gridName).data("kendoGrid");

    if (kendoGrid !== undefined) {
      kendoGrid.destroy();
    }

    let gridOptions: kendo.ui.GridOptions = {};
    gridOptions.dataSource = dataSource;
    gridOptions.sortable = true;
    gridOptions.selectable = true;
    gridOptions.reorderable = true;
    gridOptions.scrollable = true;
    gridOptions.noRecords = {
      template: '<p class="center no-data-message">NO DATA</p>',
    };
    gridOptions.pageable = false;
    if (window.innerWidth > 768) {
      gridOptions.filterMenuInit = CustomGridFilterModule.onFilterMenuInit;
    }
    gridOptions.filterMenuInit = function (e) {
      if (e.container.find("select:eq(0)>option").length > 1) {
        e.container.find("select:eq(0)>option")[1].remove();
        //e.container.find("select:eq(1)>option")[1].remove();
        //e.container.find("select:eq(2)>option")[0].remove();
      }
    };
    gridOptions.groupable = true;
    gridOptions.resizable = true;
    gridOptions.filterable = true;
    gridOptions.columnMenu = true;
    gridOptions.toolbar = [
      "excel",
      "pdf",
      this._authService.UserInfo.Roles.includes("InsaUser")
        ? {
            template:
              "<button type='button' id='custom-grid-wizard-toggle' class='k-button k-button-icontext'><span class='k-icon custom-grid-wizard'></span>Grid Wizard</button>",
          }
        : null,
    ];

    gridOptions.editable = {
      mode: "inline",
    };

    gridOptions.edit = function (e) {
      $("#" + this.gridName)
        .data("kendoGrid")
        .cancelChanges();
      if (e.model.isNew()) {
        this.onSelect("new");
      } else {
        const entityId = e.model[this.keyFields[0]];
        this.selectedId = e.model[entityId];
        this.onSelect(entityId);
      }
    }.bind(this);

    gridOptions.remove = function (evt) {
      $("#" + this.gridName)
        .data("kendoGrid")
        .cancelChanges();
      const entityId = evt.model[this.keyFields[0]];
      this.deleteItem(entityId);
    }.bind(this);

    gridOptions.columnReorder = customGroupHeader.gridColumnReorder;
    //columnDefs.push( { command: [{ name: "edit", text: "edit" }], title: "&nbsp;", width: "40px" });
    if (this.canAdd) {
      gridOptions.toolbar.unshift({
        template: `<button type='button' class='k-button k-button-icontext custom-add-new'>
               <span class='k-icon k-i-plus'></span> ADD NEW
             </button>`,
      });
    }
    if (this.canDelete) {
      //columnDefs.push( { command: ["edit", "destroy"], title: "&nbsp;", width: "250px" });
      (columnDefs.push({
        command: [{ name: "edit", className: "k-icon k-i-edit" }],
        title: "&nbsp;",
        width: 25,
      }),
        columnDefs.push({
          command: [{ name: "delete", className: "k-icon k-i-delete" }],
          title: "&nbsp;",
          width: 25,
        }));
    }

    columnDefs.forEach((col) => {
      if (col.field) {
        col.groupHeaderTemplate = `#= getGroupInfo(aggregates, data, getKendoGrid('${this.gridName}')) #`;
      }
    });
    gridOptions.columns = columnDefs;
    this.setColumnAttributes(gridOptions.columns, metadataDetails);

    gridOptions.dataBound = customGroupHeader.KendoGridDataBound;

    // SharedMethodsModule.saveGridOptionsOnChange(gridOptions, this.gridName, this.metadataName)

    // const savedOptions = SharedMethodsModule.getSavedGridOptions(columnDefs, this.gridName, this.metadataName)
    // const combinedGridOptions = SharedMethodsModule.combineGridInitOptions(savedOptions, gridOptions)

    console.log(gridOptions);

    $("#" + this.gridName).kendoGrid(gridOptions);
    kendoGrid = $("#" + this.gridName).data("kendoGrid");

    $("#" + this.gridName).off("click", ".custom-add-new");
    $("#" + this.gridName).on("click", ".custom-add-new", (e) => {
      e.preventDefault();
      this._zone.run(() => {
        this._router.navigate(["new"], { relativeTo: this.route });
      });
    });
    // if (savedOptions && savedOptions.dataSource) {
    //   $('#' + this.gridName).data('kendoGrid').dataSource.group(savedOptions.dataSource.group)
    //   $('#' + this.gridName).data('kendoGrid').dataSource.sort(savedOptions.dataSource.sort)
    // }

    setTimeout(() => {
      kendoGrid = $("#" + this.gridName).data("kendoGrid");
      const options = kendoGrid.getOptions();
      console.log(options);
    }, 1000);

    if (this.metadataName === "TaskList") {
      dataSource.filter({
        logic: "IsEqualTo",
        // filters: [{ field: 'TaskCompleted', value: false }],
        filters: [{ field: "Done", value: false }],
      });

      dataSource.group({
        // field: 'AssignedUserID',
        field: "AssignedTo",
      });

      dataSource.sort([{ field: "DueDate", dir: "asc" }]);
    } else if (this.metadataName === "PortalLoginStats") {
      dataSource.sort([{ field: "SessionStart", dir: "desc" }]);
    }

    setTimeout(() => {
      $("#custom-grid-wizard-toggle").off("click", this.openGridWizard);
      $("#custom-grid-wizard-toggle").click(this.openGridWizard);
    }, 10);
  }

  setColumnAttributes(
    columns: kendo.ui.GridColumn[],
    metadataDetails: ParsedFieldMetadataDetail[],
  ) {
    columns.forEach((col) => {
      if (col.field) {
        const md = metadataDetails.find((md) => md.FieldName === col.field);
        if (md.JavascriptType === "number") {
          switch (md.DataType) {
            case "real":
            case "money":
            case "float":
            case "decimal":
            case "smallint":
            case "short":
            case "double":
              const style = "text-align: right;";
              if (col.attributes) {
                col.attributes.style = col.attributes.style
                  ? col.attributes.style + style
                  : style;
              } else {
                col.attributes = { style };
              }

              if (col.headerAttributes) {
                col.headerAttributes.style = col.headerAttributes.style
                  ? col.headerAttributes.style + style
                  : style;
              } else {
                col.headerAttributes = { style };
              }

              if (col.footerAttributes) {
                col.footerAttributes.style = col.footerAttributes.style
                  ? col.footerAttributes.style + style
                  : style;
              } else {
                col.footerAttributes = { style };
              }
          }
        }
      }
    });
  }

  handleError(message?) {
    this.showModal({
      title: "Oops!",
      message: message || "Something went wrong",
    });
    this.loadingStopped();
  }

  loadingStarted() {
    kendo.ui.progress($(".genericForm-loading"), true);
    $("#" + this.gridName).addClass("k-state-disabled");
  }

  loadingStopped() {
    kendo.ui.progress($(".genericForm-loading"), false);
    $("#" + this.gridName).removeClass("k-state-disabled");
  }

  ngOnInit() {
    document.getElementById("content").classList.add("flex-override"); // fixes vertical overflow issue
  }

  ngOnChanges(changes) {
    // CLEAR GRID
    const kendoGrid = $("#" + this.gridName).data("kendoGrid");
    if (kendoGrid) {
      kendoGrid.destroy();
    }
    $("#" + this.gridName).empty();

    if (changes.metadataName && changes.metadataName.currentValue) {
      // LOAD METADATA AND DEFAULT LAYOUT
      this.loadingStarted();
      const metadataObservable =
        this._insaMetadataService.getMetaDataInformation(this.metadataName);
      const defaultLayoutObservable: Observable<kendo.ui.GridOptions> =
        this._authService.loadDefaultGridLayout(
          this.gridName,
          this.metadataName,
        );

      combineLatest([metadataObservable, defaultLayoutObservable]).subscribe(
        ([metadataSet, defaultLayout]) => {
          if (metadataSet && defaultLayout) {
            const parsedMetadataSet = makeParsedMetadataSet(
              metadataSet,
              this._localize.getLocalizedResource.bind(this._localize),
            );
            this.keyFields = computeKeyFields(
              parsedMetadataSet[0].InsaMetaDataDetails,
            );
            this.metadataSet = parsedMetadataSet;

            const details = parsedMetadataSet[0].InsaMetaDataDetails;
            const primaryKeyMetadata = details.find(
              (md) => String(md.IsKey).toLowerCase() === "true",
            );
            if (primaryKeyMetadata) {
              const recordSet = metadataSet[1].MetaDataSelectedEntity;
              let gridSource = this.prepareGridSource(recordSet, metadataSet);
              const pkField = primaryKeyMetadata.FieldName;
              const values = gridSource.data
                .map((row) => row[pkField])
                .filter((val) => typeof val === "number");

              const max = Math.max(...values, 0);
              this._gridStateService.setMaxPrimaryKeyValue(max);
            }

            if (!this.keyFields.length) {
              console.warn(`${this.metadataName} metadata has no key field`);
            }
            $("#" + this.gridName).empty();
            if (defaultLayout) {
              this.prepareGenericForm(parsedMetadataSet, defaultLayout);
              this.loadingStopped();
            }
          }
          this.loadingStopped();
        },
        (err) => {
          $("#" + this.gridName).empty();
          this.handleError(err);
        },
      );
    } else if (this.data) {
      $("#" + this.gridName).kendoGrid({
        columnMenu: true,
        filterable: true,
        sortable: true,
        dataSource: { data: this.data },
      });
      this.loadingStopped();
    } else {
      console.error(
        "GenericGrid requires either data, or metadataName property",
      );
    }
  }

  ngOnDestroy() {
    document.getElementById("content").classList.remove("flex-override"); // fixes vertical overflow issue

    // remove event listeners
    $("#" + this.gridName).off("dblclick", this.doubleClickHandler);
    $("html").off("keydown", this.delPressHandler);
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-generic-form/generic-grid/generic-grid.component.ts